/**
 * Phase 220 — Super Admin In-App Walkthrough Tutorial Manager
 *
 * Lets Out-Play HQ paste a YouTube / Facebook / Instagram / LinkedIn / Vimeo
 * video URL, tag it to a role (academy_owner / employee / student / parent /
 * all), and it auto-renders inside that role's landing dashboard. The backend
 * derives a sandbox-safe `embed_url` from the raw share link at register-time.
 *
 * Endpoints used:
 *   GET    /api/super-admin/walkthroughs
 *   POST   /api/super-admin/walkthroughs
 *   PATCH  /api/super-admin/walkthroughs/{id}
 *   DELETE /api/super-admin/walkthroughs/{id}
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Building2, Plus, Trash2, Eye, EyeOff, Video, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { apiErrorToast } from '@/lib/apiErrorToast';

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'academy_owner', label: 'Academy Owner' },
  { value: 'employee', label: 'Coach / Employee' },
  { value: 'student', label: 'Student' },
  { value: 'parent', label: 'Parent' },
];

// Phase 220.b — placement keys must match VALID_WALKTHROUGH_PLACEMENTS in server.py
const PLACEMENT_OPTIONS = [
  { value: 'dashboard',  label: 'Dashboard',  hint: "Role landing page (/dashboard, /coach)" },
  { value: 'help_center', label: 'Help Center', hint: '/help — for technical / how-to clips' },
  { value: 'my_portal',  label: 'My Portal',  hint: '/student/profile, /parent — self-profile views' },
];

function badgeForRole(role) {
  const map = {
    all: 'bg-slate-200 text-slate-800',
    academy_owner: 'bg-emerald-100 text-emerald-700',
    employee: 'bg-indigo-100 text-indigo-700',
    student: 'bg-amber-100 text-amber-700',
    parent: 'bg-pink-100 text-pink-700',
  };
  return map[role] || 'bg-slate-100 text-slate-700';
}

function safeHostname(rawUrl) {
  try { return new URL(rawUrl).hostname.replace(/^www\./, ''); }
  catch { return ''; }
}

export default function SuperAdminWalkthroughs() {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '',
    raw_url: '',
    target_role: 'academy_owner',
    target_placements: ['dashboard'],
    description: '',
  });
  const [previewVideoId, setPreviewVideoId] = useState(null);

  const togglePlacement = (loc) => {
    setForm((f) => {
      const has = f.target_placements.includes(loc);
      const next = has
        ? f.target_placements.filter((p) => p !== loc)
        : [...f.target_placements, loc];
      // Always preserve at least one placement so the row is queryable
      return { ...f, target_placements: next.length ? next : f.target_placements };
    });
  };

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/super-admin/walkthroughs');
      setVideos(r.data?.entries || []);
    } catch (e) { apiErrorToast(e, 'Failed to load walkthroughs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    // Defer initial fetch past the synchronous effect run (React 19 guard)
    const t = setTimeout(() => { refresh(); }, 0);
    return () => clearTimeout(t);
  }, [refresh]);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.raw_url.trim()) {
      toast.error('Title and video URL are required');
      return;
    }
    if (!form.target_placements || form.target_placements.length === 0) {
      toast.error('Pick at least one placement (Dashboard / Help Center / My Portal)');
      return;
    }
    setCreating(true);
    try {
      await api.post('/super-admin/walkthroughs', form);
      toast.success('Walkthrough added and live on the chosen placements');
      setForm({
        title: '',
        raw_url: '',
        target_role: 'academy_owner',
        target_placements: ['dashboard'],
        description: '',
      });
      await refresh();
    } catch (e) { apiErrorToast(e, 'Failed to add walkthrough'); }
    finally { setCreating(false); }
  };

  const handleToggleActive = async (video) => {
    try {
      await api.patch(`/super-admin/walkthroughs/${video.id}`, { is_active: !video.is_active });
      toast.success(video.is_active ? 'Walkthrough hidden from dashboards' : 'Walkthrough is now live');
      await refresh();
    } catch (e) { apiErrorToast(e, 'Failed to update walkthrough'); }
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`Permanently delete "${video.title}"?`)) return;
    try {
      await api.delete(`/super-admin/walkthroughs/${video.id}`);
      toast.success('Walkthrough deleted');
      await refresh();
    } catch (e) { apiErrorToast(e, 'Failed to delete walkthrough'); }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="super-admin-walkthroughs-denied">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
          <Building2 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
          <h2 className="text-lg font-display font-semibold mb-2">Access Denied</h2>
          <p className="text-sm text-muted-foreground">Super Admin access required.</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="super-admin-walkthroughs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <Video className="h-6 w-6 text-indigo-600" /> In-App Walkthrough Library
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Paste any YouTube / Facebook / Instagram / LinkedIn video link, tag a role,
            and it auto-mounts on that role&apos;s landing dashboard.
          </p>
        </div>
        <Badge variant="outline" data-testid="walkthrough-count-badge">{videos.length} total</Badge>
      </div>

      {/* Create form */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="h-4 w-4 text-emerald-600" /> Add a Walkthrough
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Title</Label>
              <Input
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Academy Owner Walkthrough — Roster & Billing"
                data-testid="walkthrough-form-title"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Target Role</Label>
              <Select
                value={form.target_role}
                onValueChange={(v) => setForm({ ...form, target_role: v })}
              >
                <SelectTrigger data-testid="walkthrough-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Raw Share URL</Label>
            <Input
              value={form.raw_url}
              onChange={e => setForm({ ...form, raw_url: e.target.value })}
              placeholder="Paste YouTube / Facebook / Instagram / LinkedIn / Vimeo link"
              data-testid="walkthrough-form-url"
            />
            <p className="text-[11px] text-muted-foreground">
              We auto-derive a sandbox-safe iframe embed URL.
            </p>
          </div>
          {/* Phase 220.b — placement multi-select */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Target Placements</Label>
            <div className="flex flex-wrap gap-2" data-testid="walkthrough-form-placements">
              {PLACEMENT_OPTIONS.map((p) => {
                const checked = form.target_placements.includes(p.value);
                return (
                  <button
                    type="button"
                    key={p.value}
                    onClick={() => togglePlacement(p.value)}
                    data-testid={`walkthrough-form-placement-${p.value}`}
                    className={`text-xs font-bold px-3 py-1.5 rounded-lg border-2 transition-all flex items-center gap-1.5 ${
                      checked
                        ? 'bg-indigo-600 text-white border-indigo-700 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-400'
                    }`}
                    title={p.hint}
                  >
                    <span className={`h-3.5 w-3.5 rounded border-2 inline-flex items-center justify-center ${
                      checked ? 'bg-white border-white' : 'border-slate-400'
                    }`}>
                      {checked && <span className="block h-1.5 w-1.5 rounded-sm bg-indigo-600" />}
                    </span>
                    {p.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Where in the UI this video appears. Pick one or more — the same row can show in multiple places.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Description (optional)</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="What does this walkthrough cover?"
              data-testid="walkthrough-form-description"
            />
          </div>
          <Button
            onClick={handleCreate}
            disabled={creating || !form.title.trim() || !form.raw_url.trim()}
            data-testid="walkthrough-form-submit"
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
          >
            {creating ? (<><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Adding…</>) :
              (<><Plus className="h-3.5 w-3.5 mr-2" /> Add Walkthrough</>)}
          </Button>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Library</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground" data-testid="walkthrough-empty-state">
              No walkthroughs yet. Add one above and it&apos;ll appear on the chosen role&apos;s dashboard immediately.
            </div>
          ) : (
            <div className="space-y-3" data-testid="walkthrough-list">
              {videos.map(v => (
                <div
                  key={v.id}
                  data-testid={`walkthrough-item-${v.id}`}
                  className={`border rounded-xl p-4 flex flex-col sm:flex-row gap-4 ${
                    v.is_active ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-70'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${badgeForRole(v.target_role)}`}>
                        {v.target_role.toUpperCase().replace('_', ' ')}
                      </span>
                      {/* Phase 220.b — placement chips */}
                      {(v.target_placements || ['dashboard']).map((p) => (
                        <span
                          key={p}
                          className="text-[10px] font-bold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
                          data-testid={`walkthrough-placement-chip-${p}`}
                        >
                          @ {p.replace('_', ' ')}
                        </span>
                      ))}
                      <span className="text-[10px] text-slate-500">{safeHostname(v.raw_url)}</span>
                      {!v.is_active && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-300 text-slate-700">HIDDEN</span>}
                    </div>
                    <h3 className="font-bold text-sm text-slate-900">{v.title}</h3>
                    {v.description && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{v.description}</p>}
                    <a
                      href={v.raw_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:underline mt-1.5"
                    >
                      Open original <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPreviewVideoId(previewVideoId === v.id ? null : v.id)}
                      data-testid={`walkthrough-preview-${v.id}`}
                    >
                      {previewVideoId === v.id ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </Button>
                    <div className="flex items-center gap-1.5">
                      <Switch
                        checked={!!v.is_active}
                        onCheckedChange={() => handleToggleActive(v)}
                        data-testid={`walkthrough-toggle-${v.id}`}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(v)}
                      data-testid={`walkthrough-delete-${v.id}`}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  {previewVideoId === v.id && (
                    <div className="w-full sm:w-72 aspect-video rounded-lg overflow-hidden border border-slate-200 bg-slate-950 shrink-0">
                      <iframe
                        src={v.embed_url}
                        title={v.title}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
